import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

import { errorMessage, RequestError } from '../../MarketExplorer/api';
import {
  Budget,
  CreateThreadResponse,
  CurateResponse,
  Job,
  JobStatus,
  MemoryList,
  Proposal,
  ProposalVersion,
  RejectPayload,
  SavePayload,
  SaveResponse,
  SendMessageResponse,
  ThreadDetail,
  ThreadList,
} from './types';

/**
 * The blueprint builder client (pm.api `documentation/BLUEPRINT_BUILDER_API.md`).
 *
 * Same boundary as the Corpus & news client beside it: every call goes through
 * `apiCaller`, so auth refresh and the trailing slash the Django routes want
 * are applied in one place, and a non-2xx answer becomes a `RequestError`
 * carrying the status — the composer needs to tell a 409 ("a turn is already
 * running") from a failure.
 */

export const BASE = '/scenario-builder/admin/builder';

type Method = 'GET' | 'POST' | 'DELETE';

const request = async <Result>(
  path: string,
  method: Method = 'GET',
  data: Record<string, unknown> = {},
  signal?: AbortSignal,
): Promise<Result> => {
  const response = await apiCaller({
    path,
    method,
    data,
    signal,
    apiUrl: API_URL,
  });
  if (response.status < 200 || response.status >= 300) {
    throw new RequestError(
      errorMessage(response.result) ||
        (response.status === 409
          ? 'A turn is already running on this chat. Wait for it to finish.'
          : 'The request could not be completed. Please try again.'),
      response.status,
    );
  }
  return response.result as Result;
};

/* ------------------------------------------------------------------ *
 * Threads
 * ------------------------------------------------------------------ */

export const listThreads = (signal?: AbortSignal) =>
  request<ThreadList>(`${BASE}/threads`, 'GET', {}, signal);

/** With text, the first turn starts at once and its job comes back. */
export const createThread = (text?: string) =>
  request<CreateThreadResponse>(
    `${BASE}/threads`,
    'POST',
    text && text.trim() ? { text } : {},
  );

export const fetchThread = (id: number, signal?: AbortSignal) =>
  request<ThreadDetail>(`${BASE}/threads/${id}`, 'GET', {}, signal);

/** 409 while a turn is already running on the thread. */
export const sendMessage = (threadId: number, text: string) =>
  request<SendMessageResponse>(`${BASE}/threads/${threadId}/messages`, 'POST', {
    text,
  });

/* ------------------------------------------------------------------ *
 * Proposals and versions
 * ------------------------------------------------------------------ */

export const fetchProposal = (id: number, signal?: AbortSignal) =>
  request<Proposal>(`${BASE}/proposals/${id}`, 'GET', {}, signal);

export const fetchVersion = (id: number, signal?: AbortSignal) =>
  request<ProposalVersion>(`${BASE}/versions/${id}`, 'GET', {}, signal);

export const rejectProposal = (id: number, payload: RejectPayload) =>
  request<Proposal>(`${BASE}/proposals/${id}/reject`, 'POST', {
    reasons: payload.reasons,
    ...(payload.note && payload.note.trim()
      ? { note: payload.note.trim() }
      : {}),
  });

export const saveProposal = (id: number, payload: SavePayload) =>
  request<SaveResponse>(`${BASE}/proposals/${id}/save`, 'POST', {
    ...payload,
  });

export const curateVersion = (id: number) =>
  request<CurateResponse>(`${BASE}/versions/${id}/curate`, 'POST', {});

export const fetchBudget = (signal?: AbortSignal) =>
  request<Budget>(`${BASE}/budget`, 'GET', {}, signal);

/* ------------------------------------------------------------------ *
 * Long-term memories
 * ------------------------------------------------------------------ */

export const listMemories = (signal?: AbortSignal) =>
  request<MemoryList>(`${BASE}/memories`, 'GET', {}, signal);

/** 204 with no body: the memory is gone. */
export const deleteMemory = (id: number) =>
  request<null>(`${BASE}/memories/${id}`, 'DELETE');

/* ------------------------------------------------------------------ *
 * Job vocabulary and polling
 * ------------------------------------------------------------------ */

/** How often the chat, a save and a curation are re-read. */
export const POLL_MS = 2000;

/** Past this a panel stops watching; the work carries on server-side. */
export const POLL_TIMEOUT_MS = 10 * 60 * 1000;

/** One dropped read is not an outcome; this many in a row is. */
export const POLL_READ_RETRIES = 3;

const status = (job: Job | null | undefined): JobStatus | '' =>
  (String(job?.status || '').toLowerCase() as JobStatus) || '';

/** Statuses compared case-insensitively: the job ledger speaks upper case. */
export const isJobLive = (job: Job | null | undefined) =>
  status(job) === 'queued' || status(job) === 'running';

export const isJobFailed = (job: Job | null | undefined) =>
  ['failed', 'refused', 'lost', 'canceled'].includes(status(job));

/** The sentence a failed, refused, lost or canceled job is shown with. */
export const jobMessage = (job: Job): string => {
  const own =
    status(job) === 'refused'
      ? job.refusal?.message || job.error?.message
      : job.error?.message || job.refusal?.message;
  if (own) return own;
  switch (status(job)) {
    case 'refused':
      return 'The builder refused this request.';
    case 'lost':
      return 'The worker running this turn stopped before it finished.';
    case 'canceled':
      return 'This turn was canceled.';
    default:
      return 'The builder failed while working on this turn.';
  }
};

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

export type PollOptions<Value> = {
  onUpdate?: (value: Value) => void;
  intervalMs?: number;
  timeoutMs?: number;
  /** Set true by a closing panel so the poll stops walking. */
  cancelled?: () => boolean;
};

/**
 * Re-read something every `intervalMs` until `done` says so, the panel goes
 * away, or the timeout passes (the last value comes back either way). The
 * `runJob` walk in `actionsApi.ts`, at the builder's flat two seconds.
 */
export const pollUntil = async <Value>(
  read: () => Promise<Value>,
  done: (value: Value, reads: number) => boolean,
  initial: Value,
  {
    onUpdate,
    intervalMs = POLL_MS,
    timeoutMs = POLL_TIMEOUT_MS,
    cancelled,
  }: PollOptions<Value> = {},
): Promise<Value> => {
  let value = initial;
  const startedAt = Date.now();
  let reads = 0;
  let failures = 0;
  while (!done(value, reads)) {
    if (cancelled?.() || Date.now() - startedAt >= timeoutMs) return value;
    await sleep(intervalMs);
    if (cancelled?.()) return value;
    try {
      value = await read();
      reads += 1;
      failures = 0;
    } catch (readError) {
      failures += 1;
      if (failures > POLL_READ_RETRIES) throw readError;
      continue;
    }
    onUpdate?.(value);
  }
  return value;
};

export const isSaveSettled = (proposal: Proposal) =>
  proposal.saved?.status === 'saved' || proposal.saved?.status === 'failed';

/** After `save/`, walk the proposal until the draft blueprint exists or not. */
export const waitForSave = (
  proposal: Proposal,
  options: PollOptions<Proposal> = {},
) =>
  pollUntil(() => fetchProposal(proposal.id), isSaveSettled, proposal, options);

export const pendingCurations = (version: ProposalVersion) =>
  version.preview.turns.reduce(
    (count, turn) =>
      count +
      turn.news.filter(item => item.curation?.status === 'pending').length,
    0,
  );

/**
 * After `curate/`, walk the version until no headline is `pending`.
 *
 * A read with nothing pending ends the walk only once some item carries a
 * curation, or after three such reads: a backend that marks the items pending
 * a moment after answering 202 must not end the walk on the first read.
 */
export const waitForCuration = (
  version: ProposalVersion,
  options: PollOptions<ProposalVersion> = {},
) =>
  pollUntil(
    () => fetchVersion(version.id),
    (value, reads) => {
      if (reads === 0) return false;
      if (pendingCurations(value) > 0) return false;
      const curated = value.preview.turns.some(turn =>
        turn.news.some(item => item.curation),
      );
      return curated || reads >= 3;
    },
    version,
    options,
  );
